import {createSelector} from 'reselect';
import lodashFromPairs from 'lodash/fromPairs';
import lodashToPairs from 'lodash/toPairs';
import {getConfiguration} from './configuration-selectors';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import lodashValues from 'lodash/values';
import lodashPick from 'lodash/pick';
import lodashPickBy from 'lodash/pickBy';
import {getSubtypesTreeByPredicate, sortIssueTypeByOrderIndex} from '../../utils/issueTypes-helpers';

export const getIssueTypesAdminEntitiesMap = state => state.issueTypes.entitiesMap;
export const getIssueTypesAdminIds = state => state.issueTypes.issueTypeIds;
export const isErrorLoadingIssueTypesAdminList = state => !!state.issueTypes.error;
export const getErrorLoadingIssueTypesAdminList = state => state.issueTypes.error;
export const isLoadingIssueTypesAdminList = state => state.issueTypes.loading;
export const isCreatingIssueTypesAdmin = state => state.issueTypes.isCreating;
export const getSelectIssueTypeForEdit = state => state.issueTypes.selectedIssueTypeForEdit;
export const isUpdatingIssueTypesAdmin = state => state.issueTypes.isUpdating;
export const isIssueTypesAdminListFetched = state => state.issueTypes.fetched;
export const getSelectedIssueTypeAttributes = state => state.issueTypes.selectedIssueTypeAttributes;
export const getPendingIssueType = state => state.issueTypes.pendingIssueType;
export const isIssueTypesAdminEditModalOpen = state => state.issueTypes.editModalOpen;
export const isIssueTypesAdminAddAttributesModalOpen = state => state.issueTypes.addAttributesModalOpen;
export const isIssueTypesAdminLeaveAddAttributesModalOpen = state => state.issueTypes.leaveAddAttributesModalOpen;

export const getContainerId = createSelector(
  getConfiguration,
  ({jsonapi}) => jsonapi && jsonapi.containerId
);

export const getReportContainerId = createSelector(
  getConfiguration,
  ({jsonapi}) => jsonapi.reportContainerId
);

export const getLBSContainerId = createSelector(
  getConfiguration,
  ({jsonapi}) => jsonapi.lbsContainerId
);

export const getIssueTypesAdminEntitiesMapWithLabelValueMap = createSelector(
  getIssueTypesAdminEntitiesMap,
  entities =>
    entities &&
    lodashFromPairs(
      lodashToPairs(entities).map(([key, entity]) => [
        key,
        {
          ...entity,
          label: entity.title,
          value: entity.id
        }
      ])
    )
);

export const getIssueTypeNames = createSelector(
  getIssueTypesAdminEntitiesMap,
  getIssueTypesAdminIds,
  (entitiesMap, issueTypeIds) => issueTypeIds.map(id => entitiesMap[id].title.toLowerCase())
);

export const getIssueTypeIdsWithoutContainer = createSelector(
  getIssueTypesAdminIds,
  getContainerId,
  (typeIds, containerId) => typeIds.filter(id => id !== containerId)
);

export const getIssueSubTypeNames = createSelector(
  getIssueTypesAdminEntitiesMap,
  (state, childrenIds) => childrenIds,
  (entitiesMap, childrenIds) => childrenIds.map(id => entitiesMap[id].title.toLowerCase())
);

export const getIssueTypeById = createSelector(
  getIssueTypesAdminEntitiesMap,
  (state, id) => id,
  (entitiesMap, id) => entitiesMap[id]
);

export const getIssueTypeForEdit = createSelector(
  state => state,
  getSelectIssueTypeForEdit,
  (state, selectedIssueTypeId) => {
    if (isNullOrUndefined(selectedIssueTypeId)) {
      return null;
    }
    return getIssueTypeById(state, selectedIssueTypeId);
  }
);

export const getFieldIssueTypesV2IdToTitleMap = createSelector(
  getIssueTypesAdminEntitiesMap,
  getIssueTypeIdsWithoutContainer,
  (entitiesMap, issueTypeIds) => {
    const issueTypes = {};
    issueTypeIds.forEach(id => {
      issueTypes[id] = entitiesMap[id].title;
    });
    return issueTypes;
  }
);

export const getFieldIssueTypesAndSubTypesIdToTitleMap = createSelector(
  getIssueTypesAdminEntitiesMap,
  entitiesMap => {
    const issueTypes = {};
    lodashValues(entitiesMap).forEach(entity => {
      issueTypes[entity.id] = entity.title;
    });
    return issueTypes;
  }
);

const getFieldIssueTypesEntities = createSelector(
  getIssueTypesAdminEntitiesMap,
  getIssueTypeIdsWithoutContainer,
  (entitiesMap, issueTypeIds) => lodashPick(entitiesMap, issueTypeIds)
);

const getFieldIssueSubTypesEntities = createSelector(
  getIssueTypesAdminEntitiesMap,
  entitiesMap => lodashPickBy(entitiesMap, entity => !!entity.parentId)
);

export const getActiveFieldIssueTypes = createSelector(
  getFieldIssueTypesEntities,
  issueTypesEntities =>
    lodashValues(lodashPickBy(issueTypesEntities, entity => entity.isActive)).sort(sortIssueTypeByOrderIndex)
);

export const getActiveFieldIssueSubTypes = createSelector(
  getFieldIssueSubTypesEntities,
  subTypesEntitiesMap =>
    lodashValues(lodashPickBy(subTypesEntitiesMap, entity => entity.isActive)).sort(sortIssueTypeByOrderIndex)
);

export const getActiveFieldIssueSubTypesByParentId = createSelector(
  getActiveFieldIssueSubTypes,
  (state, issueTypeId) => issueTypeId,
  (subTypes, issueTypeId) => subTypes.filter(subType => subType.parentId === issueTypeId)
);

export const getActiveIssueSubTypesGroupedDropdownOptions = createSelector(
  getIssueTypesAdminEntitiesMap,
  getActiveFieldIssueTypes,
  (entitiesMap, activeFieldIssueTypes) =>
    getSubtypesTreeByPredicate(typeOrSubtype => typeOrSubtype.isActive, entitiesMap, activeFieldIssueTypes)
);

export const getActiveIssueSubTypesGroupedDropdownOptionsWithLabelValueMap = createSelector(
  getActiveIssueSubTypesGroupedDropdownOptions,
  options =>
    options.map(opt => [
      opt[0],
      opt[1].map(type => ({
        ...type,
        label: type.title,
        value: type.id
      }))
    ])
);

// Used to get current issue type selected definition ids
export const getSelectedIssueTypeAttributeDefinitionIds = createSelector(
  getSelectedIssueTypeAttributes,
  mappings => mappings.map(mapping => mapping.attributeDefinitionId)
);

export const getDefaultIssueSubtypeId = createSelector(
  getActiveFieldIssueTypes,
  getIssueTypesAdminEntitiesMap,
  (activeFieldIssueTypes, fieldIssueTypeEntitiesMap) => {
    const defaultType = activeFieldIssueTypes.find(({title}) => title.toLowerCase() === 'design');
    if (
      defaultType &&
      fieldIssueTypeEntitiesMap[defaultType.immediateSubTypeId] &&
      fieldIssueTypeEntitiesMap[defaultType.immediateSubTypeId].isActive
    ) {
      return defaultType.immediateSubTypeId;
    }

    return activeFieldIssueTypes[0].immediateSubTypeId;
  }
);

const getIssueTypesWithLabelAndValueWithoutContainer = createSelector(
  getIssueTypesAdminEntitiesMapWithLabelValueMap,
  getIssueTypeIdsWithoutContainer,
  (entititesMap, ids) => ids.map(id => entititesMap[id])
);

export const getSelectedSubtypeWithLabelAndValue = createSelector(
  getIssueTypesAdminEntitiesMapWithLabelValueMap,
  (state, selectedOptionId) => selectedOptionId,
  (entititesMap, selectedOptionId) => entititesMap[selectedOptionId]
);

export const getIssueSubTypesGroupedDropdownOptionsWithLabelValueMapIncludingCurrent = createSelector(
  getIssueTypesAdminEntitiesMapWithLabelValueMap,
  getIssueTypesWithLabelAndValueWithoutContainer,
  getSelectedSubtypeWithLabelAndValue,
  (issueTypesAdminEntititesMapWithLabelValueMap, issueTypes, selectedSubtype) => {
    const isActiveOrSelected = typeOrSubtype =>
      typeOrSubtype.isActive ||
      typeOrSubtype.id === selectedSubtype.id ||
      typeOrSubtype.id === selectedSubtype.parentId;

    return getSubtypesTreeByPredicate(isActiveOrSelected, issueTypesAdminEntititesMapWithLabelValueMap, issueTypes);
  }
);
